import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate, getTodayISO } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SendMaterial() {
  const [approvedPOs, setApprovedPOs] = useState([]);
  const [poItemsToSend, setPOItemsToSend] = useState([]);
  const [materialSent, setMaterialSent] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const [sendForm, setSendForm] = useState({
    quantity_sent: '',
    sent_date: getTodayISO(),
    challan_number: '',
    vehicle_number: '',
    remarks: ''
  });

  useEffect(() => {
    fetchApprovedPOs();
    fetchMaterialSent();
  }, []);

  const fetchApprovedPOs = async () => {
    try {
      const response = await axios.get(`${API}/jobwork/pos`);
      // Filter only Approved POs
      const approved = response.data.filter(po => 
        po.approval_status === 'Approved' && po.status !== 'Completed' && po.status !== 'Rejected'
      );
      setApprovedPOs(approved);
      
      // Build list of items that need material sent (Job Work type only)
      const itemsToSend = [];
      for (const po of approved) {
        try {
          const detailsRes = await axios.get(`${API}/jobwork/pos/${po.id}`);
          const details = detailsRes.data;
          
          // Get items that are Job Work type and have pending material to send
          if (details.items_status) {
            for (const item of details.items_status) {
              if (item.item_type === 'Job Work') {
                const pendingToSend = item.quantity - item.sent;
                if (pendingToSend > 0) {
                  itemsToSend.push({
                    po_id: po.id,
                    po_number: po.po_number,
                    vendor_name: po.vendor_name,
                    po_item_id: item.id,
                    partcode: item.partcode,
                    description: item.description,
                    item_type: item.item_type,
                    job_work_type: item.job_work_type,
                    total_quantity: item.quantity,
                    sent: item.sent,
                    pending_to_send: pendingToSend
                  });
                }
              }
            }
          }
        } catch (error) {
          console.error('Error fetching PO details:', error);
        }
      }
      setPOItemsToSend(itemsToSend);
    } catch (error) {
      console.error('Error fetching POs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterialSent = async () => {
    try {
      const response = await axios.get(`${API}/jobwork/material-sent`);
      setMaterialSent(response.data);
    } catch (error) {
      console.error('Error fetching material sent:', error);
    }
  };

  const handleSendClick = (item) => {
    setSelectedItem(item);
    setSendForm({
      quantity_sent: item.pending_to_send.toString(),
      sent_date: getTodayISO(),
      challan_number: '',
      vehicle_number: '',
      remarks: ''
    });
    setShowSendModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/jobwork/material-sent`, {
        po_id: selectedItem.po_id,
        po_number: selectedItem.po_number,
        vendor_name: selectedItem.vendor_name,
        partcode: selectedItem.partcode,
        description: selectedItem.description,
        quantity_sent: parseInt(sendForm.quantity_sent),
        sent_date: sendForm.sent_date,
        challan_number: sendForm.challan_number || null,
        vehicle_number: sendForm.vehicle_number || null,
        remarks: sendForm.remarks || null
      });
      setShowSendModal(false);
      resetForm();
      fetchApprovedPOs();
      fetchMaterialSent();
      alert('Material sent recorded successfully!');
    } catch (error) {
      console.error('Error recording material sent:', error);
      alert('Error recording material sent: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setSendForm({
      quantity_sent: '',
      sent_date: new Date().toISOString().split('T')[0],
      challan_number: '',
      vehicle_number: '',
      remarks: ''
    });
    setSelectedItem(null);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="send-material">
      <h1 className="text-3xl font-bold text-gray-900">Send Material (Job Work)</h1>
      <p className="text-gray-600">Send raw material to vendors for Job Work processing. Only Job Work type items require material to be sent.</p>

      {/* Items Pending Material Send */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Pending Material to Send</h2>
          <p className="text-sm text-gray-600 mt-1">Job Work items that require material to be sent to vendor</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Work Type</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">PO Qty</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Already Sent</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pending</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {poItemsToSend.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    No pending material to send. All Job Work items have material sent.
                  </td>
                </tr>
              ) : (
                poItemsToSend.map((item, idx) => (
                  <tr key={`${item.po_id}-${item.po_item_id}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-blue-600">{item.po_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.vendor_name}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.partcode}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.job_work_type || '-'}</td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">{item.total_quantity}</td>
                    <td className="px-6 py-4 text-sm text-right text-green-600">{item.sent}</td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-yellow-600">{item.pending_to_send}</td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => handleSendClick(item)}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg transition text-sm"
                      >
                        Send Material
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Material Sent History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Material Sent History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Sent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Code</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Challan No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle No.</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {materialSent.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No material sent yet.
                  </td>
                </tr>
              ) : (
                materialSent.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">{formatDate(record.sent_date)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{record.po_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{record.vendor_name}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{record.partcode}</td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-yellow-600">{record.quantity_sent}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{record.challan_number || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{record.vehicle_number || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Send Material Modal */}
      {showSendModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Send Material to Vendor</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Item Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Item Details:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>PO Number:</strong> {selectedItem.po_number}</div>
                  <div><strong>Vendor:</strong> {selectedItem.vendor_name}</div>
                  <div><strong>Part Code:</strong> {selectedItem.partcode}</div>
                  <div><strong>Description:</strong> {selectedItem.description}</div>
                  <div><strong>Job Work Type:</strong> {selectedItem.job_work_type || '-'}</div>
                  <div><strong>PO Quantity:</strong> {selectedItem.total_quantity}</div>
                  <div><strong>Already Sent:</strong> <span className="text-green-600">{selectedItem.sent}</span></div>
                  <div><strong>Pending to Send:</strong> <span className="text-yellow-600 font-semibold">{selectedItem.pending_to_send}</span></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Sent *</label>
                  <input
                    type="date"
                    value={sendForm.sent_date}
                    onChange={(e) => setSendForm({ ...sendForm, sent_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Sent *</label>
                  <input
                    type="number"
                    value={sendForm.quantity_sent}
                    onChange={(e) => setSendForm({ ...sendForm, quantity_sent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    min="1"
                    max={selectedItem.pending_to_send}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Challan Number</label>
                  <input
                    type="text"
                    value={sendForm.challan_number}
                    onChange={(e) => setSendForm({ ...sendForm, challan_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number</label>
                  <input
                    type="text"
                    value={sendForm.vehicle_number}
                    onChange={(e) => setSendForm({ ...sendForm, vehicle_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={sendForm.remarks}
                  onChange={(e) => setSendForm({ ...sendForm, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowSendModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
                >
                  Record Material Sent
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
